import ApolloClient from 'apollo-client';
import { persistCache } from 'apollo-cache-persist';
import { WebSocketLink } from 'apollo-link-ws';
import { withClientState } from 'apollo-link-state';
import { HttpLink } from 'apollo-link-http';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import { ApolloLink, Observable, split } from 'apollo-link';
import { getMainDefinition } from 'apollo-utilities';
import { InMemoryCache } from 'apollo-cache-inmemory';
import localforage from 'localforage';
import { isTokenExpired, clearAllAuthStorage } from '../../utils/authStorage';

const urlPrefix =
  process.env.NODE_ENV !== 'production' ? 'http://localhost:5001' : '';

const cache = new InMemoryCache({});
const cacheStorage = localforage.createInstance({
  name: 'contenderCatalogStorage',
});
persistCache({
  cache,
  storage: cacheStorage,
  key: 'contender-catalog-app',
  debug: process.env.NODE_ENV !== 'production',
  maxSize: false,
});

// TODO: urls to env and fix client state and persist cache
const httpUrl = process.env.REACT_APP_HASURA_ENDPOINT_URL
  ? `${process.env.NODE_ENV !== 'production' ? 'http://' : 'https://'}${
      process.env.REACT_APP_HASURA_ENDPOINT_URL
    }/v1/graphql`
  : 'http://localhost:8080/v1/graphql';
const wsUrl = process.env.REACT_APP_HASURA_ENDPOINT_URL
  ? `${process.env.NODE_ENV !== 'production' ? 'ws://' : 'wss://'}${
      process.env.REACT_APP_HASURA_ENDPOINT_URL
    }/v1/graphql`
  : 'ws://localhost:8080/v1/graphql';

// Cache for Hasura JWT tokens
let hasuraTokenCache = null;
let tokenExpiry = 0;
let tokenExchangePromise = null; // Prevent multiple simultaneous calls

// Exchange Auth0 token for Hasura JWT
const exchangeTokenForHasuraJWT = async (getIdTokenClaims) => {
  try {
    // Check if we have a cached token that's still valid
    if (hasuraTokenCache && Date.now() / 1000 < tokenExpiry - 300) {
      // 5 minute buffer
      return hasuraTokenCache;
    }

    // If there's already a token exchange in progress, wait for it
    if (tokenExchangePromise) {
      return await tokenExchangePromise;
    }

    // Start new token exchange
    tokenExchangePromise = (async () => {
      try {
        // Check if getIdTokenClaims is a function
        if (typeof getIdTokenClaims !== 'function') {
          throw new Error('getIdTokenClaims is not a function');
        }

        const idTokenClaims = await getIdTokenClaims();

        // Check if we have valid token claims (not empty object)
        if (!idTokenClaims || Object.keys(idTokenClaims).length === 0) {
          console.error('No ID token claims received');
          return null; // Return null instead of throwing error
        }

        // Extract the raw token - it might be in different formats (same as value optimizer)
        const rawToken = idTokenClaims?.__raw || idTokenClaims;

        if (!rawToken || typeof rawToken !== 'string') {
          throw new Error('No valid ID token found in claims');
        }

        // Don't send expired tokens to exchange (stops repeated jwt expired errors)
        if (isTokenExpired(rawToken)) {
          clearAllAuthStorage();
          hasuraTokenCache = null;
          tokenExpiry = 0;
          return null;
        }
        const response = await fetch(`${urlPrefix}/auth0/exchange`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            idToken: rawToken,
          }),
        });

        if (!response.ok) {
          if (response.status === 401) {
            clearAllAuthStorage();
            hasuraTokenCache = null;
            tokenExpiry = 0;
            return null;
          }
          let errorMessage = `Token exchange failed: ${response.statusText}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
          } catch (e) {
            // If response is not JSON, use status text
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();

        // Cache the token
        hasuraTokenCache = data.idToken;
        tokenExpiry = data.expiresAt;
        // Also save to localStorage for frontend use
        localStorage.setItem('idToken', data.idToken);
        localStorage.setItem('expiresAt', data.expiresAt);
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
        }

        return data.idToken;
      } finally {
        // Clear the promise so future calls can start a new exchange
        tokenExchangePromise = null;
      }
    })();

    return await tokenExchangePromise;
  } catch (error) {
    // Clear the promise on error so retries can happen
    tokenExchangePromise = null;
    throw error;
  }
};

// Create a function that returns an Apollo client configured with Auth0 tokens
export const createAuth0ApolloClient = (getIdTokenClaims) => {
  const wsLink = new WebSocketLink(
    new SubscriptionClient(wsUrl, {
      reconnect: true,
      connectionParams: async () => {
        try {
          const hasuraToken = await exchangeTokenForHasuraJWT(getIdTokenClaims);
          if (hasuraToken) {
            return { headers: { Authorization: `Bearer ${hasuraToken}` } };
          } else {
            return { headers: {} };
          }
        } catch (error) {
          return { headers: {} };
        }
      },
    }),
  );

  const httpLink = new HttpLink({
    uri: httpUrl,
  });

  const link = split(
    // split based on operation type
    ({ query }) => {
      const { kind, operation } = getMainDefinition(query);
      return kind === 'OperationDefinition' && operation === 'subscription';
    },
    wsLink,
    httpLink,
  );

  const stateLink = withClientState({
    cache,
    defaults: {
      networkStatus: {
        __typename: 'NetworkStatus',
        isConnected: true,
      },
    },
    resolvers: {
      Query: {
        // newCustomer: (_, variables, context) => null,
      },
      Mutation: {
        updateNetworkStatus: (_, { isConnected }, { cache: _cache }) => {
          _cache.writeData({
            data: {
              networkStatus: {
                __typename: 'NetworkStatus',
                isConnected,
              },
            },
          });
          return null;
        },
      },
    },
  });

  const request = async (operation) => {
    try {
      const hasuraToken = await exchangeTokenForHasuraJWT(getIdTokenClaims);

      if (hasuraToken) {
        operation.setContext({
          headers: {
            Authorization: `Bearer ${hasuraToken}`,
          },
        });
      } else {
        operation.setContext({
          headers: {},
        });
      }
    } catch (error) {
      // You might want to redirect to login or handle this differently
      operation.setContext({
        headers: {},
      });
    }
  };

  const requestLink = new ApolloLink(
    (operation, forward) =>
      new Observable((observer) => {
        let handle;
        Promise.resolve(operation)
          .then((oper) => request(oper))
          .then(() => {
            handle = forward(operation).subscribe({
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            });
          })
          .catch(observer.error.bind(observer));

        return () => {
          if (handle) {
            handle.unsubscribe();
          }
        };
      }),
  );

  const client = new ApolloClient({
    link: ApolloLink.from([requestLink, stateLink, link]),
    cache,
    defaultOptions: {
      // graphql() hoc uses watchQuery
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
      query: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });

  // Don't reset cache - let it use cache-and-network policy to fetch fresh data

  return client;
};

// Function to clear the client instance (useful for logout)
export const clearApolloClient = () => {
  hasuraTokenCache = null;
  tokenExpiry = 0;
  tokenExchangePromise = null;
};

export default createAuth0ApolloClient;
